import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, HostListener } from "@angular/core";
import { EventViewModel } from "../../shared/models/event.model";
import { Paging } from "../../shared/paging";
import { PagingHelper } from "../../shared/pagingHelper";
import { SortOption } from "../../shared/models/sort.model";
import { LocationViewModel, NavigationTitleViewModel } from "../../shared/models";
import { Subscription } from "rxjs";
import { EventService } from "./event.service";
import { CoreLogService } from "../../services/log.service";
import { LikeService } from "../../services/like.service";
import { ActivatedRoute, Router } from "@angular/router";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { MatTabChangeEvent } from "@angular/material";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { ScrollHelper } from "../../shared/scrollHelper";
import Utils from "../../shared/utils";
import { ContentType, SortDirection, SnackBarMessageType } from "../../shared/contentTypes";
import { RouterHelperService } from "../../services/routerHelper.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { trigger, transition, query, style, stagger, animate } from "@angular/animations";

@Component({
  selector: "app-event",
  templateUrl: "./event.component.html",
  styleUrls: ["./event.component.scss"],
  providers: [EventService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("eventButtonEntering", [
      transition("false => true", [
        query(".event-button", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})
export class EventComponent implements OnInit, OnDestroy {

  public tabeventStep = ["phEvent_TabName_Upcomming", "phEvent_TabName_Expired"];
  public activeLink: string;
  public matGroupSelectedIndex: number = 0;
  public eventTabIndex: number = 0;
  private _hasLikeFilter: boolean;

  public hasData: boolean = true; // check if there is data for this page;
  public pageTitle: NavigationTitleViewModel = new NavigationTitleViewModel(); // Used as a title at the header of the page
  public currentEventList: EventViewModel[] = [];
  public expiredEventList: EventViewModel[] = [];
  public currentPaging: Paging<EventViewModel>;
  public expiredPaging: Paging<EventViewModel>;
  private pagingHelper: PagingHelper = new PagingHelper();
  private currentSortOption: SortOption[] = [];
  private expiredsortOption: SortOption[] = [];
  private filterFunction: ((item: EventViewModel) => boolean)[] = [];
  private _viewTypeSubscription: Subscription;
  public contentType = ContentType;
  public isSmallView: boolean = false;
  public isLoaded: boolean = false;

  private langSubscriber: Subscription;
  private eventChangesSubscriber: Subscription;

  constructor(private eventService: EventService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _likeService: LikeService,
    private _mainnavService: MainnavService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private internationalisationService: InternationalisationService,
    private _routerHelperService: RouterHelperService,
    private _snackBarService: SnackBarService,
    private _overlaySpinnerService: OverlaySpinnerService,
    private _scrollHelper: ScrollHelper,
    private _bottomSheetHelperService: BottomSheetHelperService) {
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("EventComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;

    me.setRouteData();

    me.langSubscriber = this.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me.onViewTypeChanges(me._mainnavService.viewType);
    me._viewTypeSubscription = me._mainnavService.observeViewTypeChanges.subscribe((viewType) => {
      me.onViewTypeChanges(viewType);
      me.PushChanges();
    });

    // reload event data if data has been changed in storage.
    me.eventChangesSubscriber = me.eventService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    me.initializeSortAndFilterOption();
    me.initializeData();
    me.matGroupSelectedIndex = 0;
    me.activeLink = me.tabeventStep[0];
  }

  private onViewTypeChanges(viewType: ViewType) {
    const me = this;
    me.isSmallView = (viewType === ViewType.Small);
  }
  private async initializeData() {
    const me = this;
    try {
      const eventIDs = await me._likeService.GetContentIDForActiveLikes(ContentType.Event);
      if (eventIDs && eventIDs.length > 0) {
        me.ReloadDataAndRender();
      } else {
        me.ReloadDataAndRender();
      }
    } catch (e) {
      me._coreLogService.error("EventComponent.initializeData", "GetContentIDForActiveLikes", e);
    }
  }

  private initializeSortAndFilterOption() {
    const me = this;
    me.currentSortOption.push(me.pagingHelper.createSortOption("momentStartDate", SortDirection.ASC));
    me.expiredsortOption.push(me.pagingHelper.createSortOption("momentStartDate", SortDirection.DESC));
  }

  /**
   * Set the title of the page by title Data in route data
   * This data has been set in RouterHelperService by each navigation Enh Evnt
   */
  private setRouteData() {
    const me = this;
    me.pageTitle = me._activatedRoute.snapshot.data["pageTitle"];
    me._hasLikeFilter = me._activatedRoute.snapshot.data["hasLikeFilter"];
  }

  /** get event data from storage
  */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me.eventService.GetEventViewModel()
      .then(event => {
        if (event) {
          if (me._hasLikeFilter) {
            event = me._likeService.filterByLike(event);
          }
          const DefaultPageSize = 4;

          let currentEventPage = 2;
          if (!Utils.IsNull(me.currentPaging)) {
            currentEventPage = me.currentPaging.CurrentPage;
          }
          me.isLoaded = true;
          me.currentPaging = new Paging(event.filter(e => !e.isExpired), DefaultPageSize, me.currentSortOption, me.filterFunction);
          me.currentEventList = [];
          me.currentEventList.push(...me.currentPaging.initLoad(currentEventPage));

          let expiredEventPage = 2;
          if (!Utils.IsNull(me.expiredPaging)) {
            expiredEventPage = me.expiredPaging.CurrentPage;
          }
          me.expiredPaging = new Paging(event.filter(e => e.isExpired), DefaultPageSize, me.expiredsortOption, me.filterFunction);
          me.expiredEventList = [];
          me.expiredEventList.push(...me.expiredPaging.initLoad(expiredEventPage));
          me.hasData = me.currentEventList.length > 0 || me.expiredEventList.length > 0;
          me.PushChanges();
        } else {
          me.hasData = false;
          me.PushChanges();
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("EventComponent.ReloadDataAndRender", "GetEventViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(28);
      });
  }

  /**
   * navigate to Event Detail
   * @param eventId Id of Event that will use for loading data in EventDetail Page
   */
  public navigateToEventDetail(eventId: number | string): void {
    const me = this;
    me._router.navigate(["/eventdetail", eventId]);
  }

  public navigateBack(): void {
    this._routerHelperService.navigateBackSimple();
  }

  public swipeLeftHandler() {
    const me = this;
    if (me.matGroupSelectedIndex === 0) {
      ++me.matGroupSelectedIndex;
      me.activeLink = me.tabeventStep[1];
      me.PushChanges();
    }
  }

  public swipeRightHandler() {
    const me = this;
    if (me.matGroupSelectedIndex === 1) {
      --me.matGroupSelectedIndex;
      me.activeLink = me.tabeventStep[0];
      me.PushChanges();
    } else if (me.matGroupSelectedIndex === 0) {
      me.navigateBack();
    }
  }

  /**
   * Handle changeTabEvent
   * @param event event Instance
   */
  public changeTab(event): void {
    const me = this;
    me.matGroupSelectedIndex = event;
    me.activeLink = me.tabeventStep[event];
    //me.tabSelectedIndexChange(event.index);
    me.PushChanges();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me.langSubscriber) {
      me.langSubscriber.unsubscribe();
    }
    if (me.eventChangesSubscriber) {
      me.eventChangesSubscriber.unsubscribe();
    }
  }

}
